import React, { useEffect, useState, useContext } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { AuthContext } from "../context/AuthContext";
import FighterRanking from "./FighterRanking";

const API_BASE_URL = "https://warfc.com/api";
const SITE_URL = "https://warfc.com";

/* =========================================================
   CLUB LOGO URL HELPER
   Handles:
   - /api/uploads/clubs/xxx.png
   - /uploads/clubs/xxx.png
   - xxx.png
   - full https:// URL
========================================================= */

const getClubLogoUrl = (logo) => {
  if (!logo) {
    return `${API_BASE_URL}/uploads/clubs/default-club.png`;
  }

  if (logo.startsWith("http://") || logo.startsWith("https://")) {
    return logo;
  }

  if (logo.startsWith("/api/")) {
    return `${SITE_URL}${logo}`;
  }

  if (logo.startsWith("/uploads/")) {
    return `${API_BASE_URL}${logo}`;
  }

  return `${API_BASE_URL}/uploads/clubs/${logo}`;
};

/* =========================================================
   EMPTY FORM
========================================================= */

const emptyForm = {
  club_name: "",
  trainer_name: "",
  location: "",
  phone: "",
  bio: "",
  founded_year: "",
  logo_file: null,
};

/* =========================================================
   COMPONENT
========================================================= */

export default function ClubRanking() {
  const { user, token } = useContext(AuthContext);

  const isAdmin = user?.role === "admin";

  const [activeTab, setActiveTab] = useState("fighters");

  const [clubs, setClubs] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingClub, setEditingClub] = useState(null);

  const [formData, setFormData] = useState(emptyForm);

  const [logoPreview, setLogoPreview] = useState(null);

  /* =======================================================
     FETCH CLUBS
  ======================================================= */

  useEffect(() => {
    if (activeTab === "clubs") {
      fetchClubs();
    }
  }, [activeTab]);

  const fetchClubs = async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await fetch(`${API_BASE_URL}/clubs`);

      if (!res.ok) {
        throw new Error(`Failed to fetch clubs: ${res.status}`);
      }

      const data = await res.json();

      const sorted = [...data]
        .sort((a, b) => {
          const rankA = Number(a.rank) || 999999;
          const rankB = Number(b.rank) || 999999;

          return rankA - rankB;
        })
        .map((club, index) => ({
          ...club,
          rank: index + 1,
        }));

      setClubs(sorted);
    } catch (err) {
      console.error("Fetch clubs error:", err);
      setError(err.message || "Failed to load clubs");
    } finally {
      setLoading(false);
    }
  };

  /* =======================================================
     OPEN ADD MODAL
  ======================================================= */

  const openAddModal = () => {
    setEditingClub(null);
    setFormData({ ...emptyForm });
    setLogoPreview(null);
    setError(null);
    setModalOpen(true);
  };

  /* =======================================================
     OPEN EDIT MODAL
  ======================================================= */

  const openEditModal = (club) => {
    setEditingClub(club);

    setFormData({
      club_name: club.club_name || "",
      trainer_name: club.trainer_name || "",
      location: club.location || "",
      phone: club.phone || "",
      bio: club.bio || "",
      founded_year: club.founded_year || "",
      logo_file: null,
    });

    setLogoPreview(
      club.logo_url ? getClubLogoUrl(club.logo_url) : null
    );

    setError(null);
    setModalOpen(true);
  };

  /* =======================================================
     CLOSE MODAL
  ======================================================= */

  const closeModal = () => {
    if (saving) return;

    setModalOpen(false);
    setEditingClub(null);
    setFormData({ ...emptyForm });
    setLogoPreview(null);
    setError(null);
  };

  /* =======================================================
     FORM CHANGE
  ======================================================= */

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  /* =======================================================
     LOGO CHANGE
  ======================================================= */

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    /* Basic validation */

    const allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/webp",
    ];

    if (!allowedTypes.includes(file.type)) {
      setError("Only JPG, PNG and WEBP images are allowed.");
      return;
    }

    /* 5MB maximum */

    if (file.size > 5 * 1024 * 1024) {
      setError("Logo image must be smaller than 5MB.");
      return;
    }

    setError(null);

    setFormData((prev) => ({
      ...prev,
      logo_file: file,
    }));

    const previewUrl = URL.createObjectURL(file);

    setLogoPreview(previewUrl);
  };

  /* =======================================================
     SAVE CLUB
  ======================================================= */

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isAdmin) {
      setError("Administrator permission required.");
      return;
    }

    if (!formData.club_name.trim()) {
      setError("Club name is required.");
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const data = new FormData();

      data.append("club_name", formData.club_name.trim());
      data.append(
        "trainer_name",
        formData.trainer_name?.trim() || ""
      );
      data.append(
        "location",
        formData.location?.trim() || ""
      );
      data.append(
        "phone",
        formData.phone?.trim() || ""
      );
      data.append(
        "bio",
        formData.bio?.trim() || ""
      );

      if (formData.founded_year) {
        data.append(
          "founded_year",
          formData.founded_year
        );
      }

      if (formData.logo_file) {
        data.append(
          "logo_file",
          formData.logo_file
        );
      }

      const url = editingClub
        ? `${API_BASE_URL}/clubs/${editingClub.club_id}`
        : `${API_BASE_URL}/clubs`;

      const method = editingClub ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: data,
      });

      const result = await res.json();

      if (!res.ok) {
        throw new Error(
          result.message ||
            `Failed to ${editingClub ? "update" : "create"} club`
        );
      }

      /* Refresh list */

      await fetchClubs();

      /* Close modal */

      setModalOpen(false);
      setEditingClub(null);
      setFormData({ ...emptyForm });
      setLogoPreview(null);
    } catch (err) {
      console.error("Save club error:", err);

      setError(
        err.message ||
          "Failed to save club."
      );
    } finally {
      setSaving(false);
    }
  };

  /* =======================================================
     DRAG END
  ======================================================= */

  const onDragEnd = async (result) => {
    if (!result.destination || !isAdmin) {
      return;
    }

    if (
      result.destination.index ===
      result.source.index
    ) {
      return;
    }

    const newClubs = Array.from(clubs);

    const [movedClub] = newClubs.splice(
      result.source.index,
      1
    );

    newClubs.splice(
      result.destination.index,
      0,
      movedClub
    );

    const rankedClubs = newClubs.map(
      (club, index) => ({
        ...club,
        rank: index + 1,
      })
    );

    /* Optimistic UI */

    setClubs(rankedClubs);

    try {
      setSaving(true);

      const res = await fetch(
        `${API_BASE_URL}/clubs/ranking`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            rankedClubs:
              rankedClubs.map((club) => ({
                club_id: club.club_id,
                rank: club.rank,
              })),
          }),
        }
      );

      if (!res.ok) {
        const result = await res.json().catch(() => ({}));

        throw new Error(
          result.message ||
            "Failed to save club ranking"
        );
      }
    } catch (err) {
      console.error(
        "Update club ranking error:",
        err
      );

      alert(
        err.message ||
          "Failed to save club ranking."
      );

      /* Restore from database */

      fetchClubs();
    } finally {
      setSaving(false);
    }
  };

  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <div className="min-h-screen bg-[#0a0a0a] pb-20">

      <div className="max-w-5xl mx-auto px-4 pt-8">

        {/* =================================================
            HEADER
        ================================================= */}

        <div className="text-center mb-10">

          <h1 className="text-5xl md:text-6xl font-black text-[#d4af37] tracking-tighter italic uppercase">
            WAR FC
          </h1>

          <p className="text-gray-500 mt-2 font-bold tracking-[0.2em] uppercase text-xs md:text-sm">
            Official Rankings
          </p>

        </div>

        {/* =================================================
            TABS
        ================================================= */}

        <div className="flex p-1 bg-gray-900/70 backdrop-blur-md rounded-xl border border-gray-800 mb-8 shadow-2xl">

          <button
            type="button"
            onClick={() =>
              setActiveTab("fighters")
            }
            className={`
              flex-1 py-3 rounded-lg
              font-black text-sm
              transition-all duration-300
              ${
                activeTab === "fighters"
                  ? "bg-[#d4af37] text-black shadow-lg"
                  : "text-gray-400 hover:text-white"
              }
            `}
          >
            FIGHTERS
          </button>

          <button
            type="button"
            onClick={() =>
              setActiveTab("clubs")
            }
            className={`
              flex-1 py-3 rounded-lg
              font-black text-sm
              transition-all duration-300
              ${
                activeTab === "clubs"
                  ? "bg-[#d4af37] text-black shadow-lg"
                  : "text-gray-400 hover:text-white"
              }
            `}
          >
            CLUBS
          </button>

        </div>

        {/* =================================================
            FIGHTER RANKING
        ================================================= */}

        {activeTab === "fighters" && (
          <FighterRanking
            isAdmin={isAdmin}
            token={token}
          />
        )}

        {/* =================================================
            CLUB RANKING
        ================================================= */}

        {activeTab === "clubs" && (
          <div>

            {/* ---------------------------------------------
                CLUB HEADER
            --------------------------------------------- */}

            <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4 mb-6 px-1">

              <div>

                <h2 className="text-xl md:text-2xl font-black text-white uppercase italic">
                  Club Standings
                </h2>

                <p className="text-gray-600 text-xs mt-1 uppercase tracking-wider">
                  Official WAR FC Club Ranking
                </p>

              </div>

              {isAdmin && (
                <button
                  type="button"
                  onClick={openAddModal}
                  className="
                    bg-white
                    text-black
                    px-5
                    py-3
                    rounded-lg
                    font-black
                    text-xs
                    tracking-wide
                    hover:bg-[#d4af37]
                    transition-all
                    shadow-lg
                  "
                >
                  + ADD CLUB
                </button>
              )}

            </div>

            {/* ---------------------------------------------
                SAVING RANK
            --------------------------------------------- */}

            {saving && !modalOpen && (
              <div className="text-center text-xs animate-pulse text-[#d4af37] mb-5 uppercase font-bold tracking-widest">
                Updating...
              </div>
            )}

            {/* ---------------------------------------------
                ERROR
            --------------------------------------------- */}

            {error && !modalOpen && (
              <div className="mb-5 p-4 rounded-lg bg-red-950/40 border border-red-900 text-red-400 text-sm">
                {error}
              </div>
            )}

            {/* ---------------------------------------------
                LOADING
            --------------------------------------------- */}

            {loading ? (
              <div className="py-20 text-center">

                <div className="inline-block w-8 h-8 border-2 border-gray-700 border-t-[#d4af37] rounded-full animate-spin" />

                <p className="text-gray-600 mt-4 text-xs uppercase tracking-widest">
                  Loading clubs...
                </p>

              </div>
            ) : clubs.length === 0 ? (

              /* -------------------------------------------
                 NO CLUBS
              ------------------------------------------- */

              <div className="py-20 text-center border border-gray-800 rounded-xl bg-gray-900/30">

                <div className="text-5xl mb-4">
                  🥊
                </div>

                <h3 className="text-white font-black uppercase">
                  No Clubs Yet
                </h3>

                {isAdmin && (
                  <button
                    type="button"
                    onClick={openAddModal}
                    className="mt-5 bg-[#d4af37] text-black px-5 py-2 rounded-lg font-black text-xs"
                  >
                    + ADD FIRST CLUB
                  </button>
                )}

              </div>

            ) : (

              /* -------------------------------------------
                 DRAG & DROP
              ------------------------------------------- */

              <DragDropContext
                onDragEnd={onDragEnd}
              >

                <Droppable droppableId="clubs">

                  {(provided) => (

                    <div
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      className="space-y-3"
                    >

                      {clubs.map(
                        (club, index) => (

                          <Draggable
                            key={club.club_id}
                            draggableId={`${club.club_id}`}
                            index={index}
                            isDragDisabled={!isAdmin}
                          >

                            {(provided, snapshot) => (

                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                className={`
                                  group
                                  relative
                                  flex
                                  items-center
                                  gap-3
                                  md:gap-6
                                  p-3
                                  md:p-4
                                  rounded-xl
                                  border
                                  transition-all
                                  duration-200
                                  ${
                                    snapshot.isDragging
                                      ? "bg-gray-800 border-[#d4af37] shadow-[0_0_20px_rgba(212,175,55,0.3)]"
                                      : "bg-gradient-to-r from-gray-900 to-black border-gray-800 hover:border-gray-600"
                                  }
                                `}
                              >

                                {/* --------------------------------
                                    DRAG HANDLE
                                --------------------------------- */}

                                {isAdmin && (
                                  <div
                                    {...provided.dragHandleProps}
                                    className="
                                      hidden
                                      sm:flex
                                      w-5
                                      text-gray-700
                                      group-hover:text-gray-400
                                      cursor-grab
                                      active:cursor-grabbing
                                      text-lg
                                      select-none
                                    "
                                    title="Drag to reorder"
                                  >
                                    ⋮⋮
                                  </div>
                                )}

                                {/* --------------------------------
                                    RANK
                                --------------------------------- */}

                                <div className="
                                  text-2xl
                                  md:text-3xl
                                  font-black
                                  italic
                                  text-gray-700
                                  group-hover:text-[#d4af37]
                                  transition-colors
                                  w-8
                                  md:w-10
                                  shrink-0
                                ">
                                  #{club.rank}
                                </div>

                                {/* --------------------------------
                                    CLUB PROFILE LINK
                                --------------------------------- */}

                                <a
                                  href={`/clubs/${club.club_id}`}
                                  className="flex items-center gap-3 md:gap-5 flex-1 min-w-0"
                                  onClick={(e) => {
                                    /*
                                      Don't interfere with
                                      drag operation.
                                    */
                                    if (
                                      snapshot.isDragging
                                    ) {
                                      e.preventDefault();
                                    }
                                  }}
                                >

                                  {/* LOGO */}

                                  <div className="relative shrink-0">

                                    <img
                                      src={getClubLogoUrl(
                                        club.logo_url
                                      )}
                                      alt={
                                        club.club_name
                                      }
                                      onError={(e) => {
                                        e.currentTarget.src =
                                          `${API_BASE_URL}/uploads/clubs/default-club.png`;
                                      }}
                                      className="
                                        w-14
                                        h-14
                                        md:w-16
                                        md:h-16
                                        rounded-lg
                                        object-cover
                                        border-2
                                        border-gray-800
                                        group-hover:border-[#d4af37]
                                        transition-all
                                        bg-black
                                      "
                                    />

                                  </div>

                                  {/* DETAILS */}

                                  <div className="flex-1 min-w-0">

                                    <h3 className="
                                      text-base
                                      md:text-xl
                                      font-black
                                      text-white
                                      uppercase
                                      tracking-tight
                                      leading-none
                                      mb-2
                                      truncate
                                    ">
                                      {club.club_name}
                                    </h3>

                                    <div className="
                                      flex
                                      flex-wrap
                                      gap-x-4
                                      gap-y-1
                                      text-[9px]
                                      md:text-[10px]
                                      font-bold
                                      uppercase
                                      tracking-wider
                                    ">

                                      <span className="text-gray-500">
                                        Coach:{" "}
                                        <span className="text-gray-300">
                                          {club.coach_name ||
                                            club.trainer_name ||
                                            "TBA"}
                                        </span>
                                      </span>

                                      {club.location && (
                                        <span className="text-gray-500">
                                          Loc:{" "}
                                          <span className="text-gray-300">
                                            {
                                              club.location
                                            }
                                          </span>
                                        </span>
                                      )}

                                      <span className="text-gray-500">
                                        Fighters:{" "}
                                        <span className="text-[#d4af37]">
                                          {club.fighter_count ??
                                            0}
                                        </span>
                                      </span>

                                    </div>

                                  </div>

                                </a>

                                {/* --------------------------------
                                    VIEW CLUB
                                --------------------------------- */}

                                <a
                                  href={`/clubs/${club.club_id}`}
                                  className="
                                    hidden
                                    md:flex
                                    items-center
                                    gap-1
                                    text-[10px]
                                    font-black
                                    uppercase
                                    text-gray-500
                                    hover:text-[#d4af37]
                                    transition-colors
                                    whitespace-nowrap
                                  "
                                >
                                  VIEW CLUB
                                  <span className="text-sm">
                                    →
                                  </span>
                                </a>

                                {/* --------------------------------
                                    ADMIN EDIT
                                --------------------------------- */}

                                {isAdmin && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();

                                      openEditModal(
                                        club
                                      );
                                    }}
                                    className="
                                      shrink-0
                                      bg-gray-800
                                      hover:bg-[#d4af37]
                                      hover:text-black
                                      text-gray-300
                                      px-3
                                      py-2
                                      rounded-lg
                                      text-[10px]
                                      font-black
                                      uppercase
                                      transition-all
                                    "
                                  >
                                    EDIT
                                  </button>
                                )}

                              </div>

                            )}

                          </Draggable>

                        )
                      )}

                      {provided.placeholder}

                    </div>

                  )}

                </Droppable>

              </DragDropContext>

            )}

          </div>
        )}

      </div>

      {/* =====================================================
          ADD / EDIT CLUB MODAL
      ===================================================== */}

      {modalOpen && (
        <div
          className="
            fixed
            inset-0
            z-50
            flex
            items-center
            justify-center
            p-4
            bg-black/80
            backdrop-blur-sm
          "
          onMouseDown={(e) => {
            if (
              e.target === e.currentTarget &&
              !saving
            ) {
              closeModal();
            }
          }}
        >

          <div
            className="
              w-full
              max-w-lg
              max-h-[90vh]
              overflow-y-auto
              bg-[#111]
              border
              border-gray-800
              rounded-2xl
              shadow-2xl
            "
          >

            {/* -----------------------------------------------
                MODAL HEADER
            ------------------------------------------------ */}

            <div className="
              flex
              items-center
              justify-between
              px-5
              py-4
              border-b
              border-gray-800
            ">

              <div>

                <h2 className="
                  text-xl
                  font-black
                  text-white
                  uppercase
                  italic
                ">
                  {editingClub
                    ? "Edit Club"
                    : "Add Club"}
                </h2>

                <p className="
                  text-gray-600
                  text-[10px]
                  uppercase
                  tracking-wider
                  mt-1
                ">
                  WAR FC Club Management
                </p>

              </div>

              <button
                type="button"
                onClick={closeModal}
                disabled={saving}
                className="
                  w-9
                  h-9
                  rounded-lg
                  bg-gray-900
                  text-gray-400
                  hover:text-white
                  hover:bg-gray-800
                  transition-all
                "
              >
                ✕
              </button>

            </div>

            {/* -----------------------------------------------
                FORM
            ------------------------------------------------ */}

            <form
              onSubmit={handleSubmit}
              className="p-5 space-y-5"
            >

              {/* ERROR */}

              {error && (
                <div className="
                  p-3
                  rounded-lg
                  bg-red-950/40
                  border
                  border-red-900
                  text-red-400
                  text-sm
                ">
                  {error}
                </div>
              )}

              {/* -------------------------------------------
                  LOGO
              -------------------------------------------- */}

              <div>

                <label className="
                  block
                  text-[10px]
                  font-black
                  text-gray-500
                  uppercase
                  tracking-wider
                  mb-2
                ">
                  Club Logo
                </label>

                <div className="
                  flex
                  items-center
                  gap-4
                  p-4
                  rounded-xl
                  bg-gray-900/60
                  border
                  border-gray-800
                ">

                  {/* Preview */}

                  <div className="
                    w-20
                    h-20
                    rounded-xl
                    bg-black
                    border
                    border-gray-700
                    flex
                    items-center
                    justify-center
                    overflow-hidden
                    shrink-0
                  ">

                    {logoPreview ? (
                      <img
                        src={logoPreview}
                        alt="Club logo preview"
                        className="
                          w-full
                          h-full
                          object-cover
                        "
                      />
                    ) : (
                      <span className="
                        text-gray-700
                        text-2xl
                      ">
                        🥊
                      </span>
                    )}

                  </div>

                  {/* Upload */}

                  <div className="flex-1">

                    <label
                      htmlFor="club-logo"
                      className="
                        inline-flex
                        items-center
                        justify-center
                        px-4
                        py-2
                        rounded-lg
                        bg-white
                        text-black
                        hover:bg-[#d4af37]
                        cursor-pointer
                        font-black
                        text-xs
                        uppercase
                        transition-all
                      "
                    >
                      {formData.logo_file
                        ? "Change Logo"
                        : editingClub
                        ? "Replace Logo"
                        : "Upload Logo"}
                    </label>

                    <input
                      id="club-logo"
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/webp"
                      onChange={handleLogoChange}
                      className="hidden"
                    />

                    <p className="
                      text-gray-600
                      text-[10px]
                      mt-2
                    ">
                      JPG, PNG or WEBP · Max 5MB
                    </p>

                  </div>

                </div>

              </div>

              {/* -------------------------------------------
                  CLUB NAME
              -------------------------------------------- */}

              <div>

                <label className="
                  block
                  text-[10px]
                  font-black
                  text-gray-500
                  uppercase
                  tracking-wider
                  mb-2
                ">
                  Club Name *
                </label>

                <input
                  type="text"
                  name="club_name"
                  value={formData.club_name}
                  onChange={handleInputChange}
                  placeholder="Enter club name"
                  required
                  className="
                    w-full
                    bg-gray-900
                    border
                    border-gray-800
                    focus:border-[#d4af37]
                    rounded-lg
                    px-4
                    py-3
                    text-white
                    outline-none
                    transition-colors
                  "
                />

              </div>

              {/* -------------------------------------------
                  TRAINER
              -------------------------------------------- */}

              <div>

                <label className="
                  block
                  text-[10px]
                  font-black
                  text-gray-500
                  uppercase
                  tracking-wider
                  mb-2
                ">
                  Head Coach / Trainer
                </label>

                <input
                  type="text"
                  name="trainer_name"
                  value={formData.trainer_name}
                  onChange={handleInputChange}
                  placeholder="Coach name"
                  className="
                    w-full
                    bg-gray-900
                    border
                    border-gray-800
                    focus:border-[#d4af37]
                    rounded-lg
                    px-4
                    py-3
                    text-white
                    outline-none
                    transition-colors
                  "
                />

              </div>

              {/* -------------------------------------------
                  LOCATION + PHONE
              -------------------------------------------- */}

              <div className="
                grid
                grid-cols-1
                sm:grid-cols-2
                gap-4
              ">

                <div>

                  <label className="
                    block
                    text-[10px]
                    font-black
                    text-gray-500
                    uppercase
                    tracking-wider
                    mb-2
                  ">
                    Location
                  </label>

                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    placeholder="Ulaanbaatar"
                    className="
                      w-full
                      bg-gray-900
                      border
                      border-gray-800
                      focus:border-[#d4af37]
                      rounded-lg
                      px-4
                      py-3
                      text-white
                      outline-none
                    "
                  />

                </div>

                <div>

                  <label className="
                    block
                    text-[10px]
                    font-black
                    text-gray-500
                    uppercase
                    tracking-wider
                    mb-2
                  ">
                    Phone
                  </label>

                  <input
                    type="text"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="94919089"
                    className="
                      w-full
                      bg-gray-900
                      border
                      border-gray-800
                      focus:border-[#d4af37]
                      rounded-lg
                      px-4
                      py-3
                      text-white
                      outline-none
                    "
                  />

                </div>

              </div>

              {/* -------------------------------------------
                  FOUNDED YEAR
              -------------------------------------------- */}

              <div>

                <label className="
                  block
                  text-[10px]
                  font-black
                  text-gray-500
                  uppercase
                  tracking-wider
                  mb-2
                ">
                  Founded Year
                </label>

                <input
                  type="number"
                  name="founded_year"
                  value={formData.founded_year}
                  onChange={handleInputChange}
                  placeholder="2020"
                  min="1900"
                  max="2100"
                  className="
                    w-full
                    bg-gray-900
                    border
                    border-gray-800
                    focus:border-[#d4af37]
                    rounded-lg
                    px-4
                    py-3
                    text-white
                    outline-none
                  "
                />

              </div>

              {/* -------------------------------------------
                  BIO
              -------------------------------------------- */}

              <div>

                <label className="
                  block
                  text-[10px]
                  font-black
                  text-gray-500
                  uppercase
                  tracking-wider
                  mb-2
                ">
                  Club Bio
                </label>

                <textarea
                  name="bio"
                  value={formData.bio}
                  onChange={handleInputChange}
                  rows={4}
                  placeholder="Short description about the club..."
                  className="
                    w-full
                    bg-gray-900
                    border
                    border-gray-800
                    focus:border-[#d4af37]
                    rounded-lg
                    px-4
                    py-3
                    text-white
                    outline-none
                    resize-none
                  "
                />

              </div>

              {/* -------------------------------------------
                  ACTIONS
              -------------------------------------------- */}

              <div className="
                flex
                gap-3
                pt-2
              ">

                <button
                  type="button"
                  onClick={closeModal}
                  disabled={saving}
                  className="
                    flex-1
                    py-3
                    rounded-lg
                    bg-gray-900
                    border
                    border-gray-800
                    text-gray-400
                    hover:text-white
                    font-black
                    text-xs
                    uppercase
                    transition-all
                  "
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={saving}
                  className="
                    flex-1
                    py-3
                    rounded-lg
                    bg-[#d4af37]
                    text-black
                    hover:bg-[#e5c45a]
                    font-black
                    text-xs
                    uppercase
                    transition-all
                    disabled:opacity-50
                    disabled:cursor-not-allowed
                  "
                >
                  {saving
                    ? "Saving..."
                    : editingClub
                    ? "Save Changes"
                    : "Add Club"}
                </button>

              </div>

            </form>

          </div>

        </div>
      )}

    </div>
  );
}